import { useCampaignStore } from '@/src/store/campaign';
import type { CampaignColorScheme } from '@/src/typings/interfaces/data/campaign';

/**
 * Recursively replaces color scheme placeholders with their actual values from the campaign color scheme.
 * A color scheme placeholder is a string that ends with "_color_scheme".
 *
 * @param value - The value to process, can be a string, array, object or any other type
 * @returns The processed value with color scheme placeholders replaced with actual colors
 */
export function recursivelyReplaceColor<T>(value: T, scheme: CampaignColorScheme | null = null): T {
  const campaignStore = useCampaignStore();

  let colorScheme = scheme;
  if (!colorScheme) {
    // If no color scheme is provided, use the one from the campaign store
    colorScheme = campaignStore?.model?.state?.config?.colorScheme || null;
  }

  if (typeof value === 'string' && value.endsWith('_color_scheme')) {
    // Extract the color name from the string (remove '_color_scheme' suffix)
    const colorName = value.replace('_color_scheme', '');

    // If the color exists in the color scheme, return it
    if (colorScheme && colorName in colorScheme && typeof colorName === 'string') {
      return colorScheme[colorName as keyof CampaignColorScheme] as unknown as T;
    }

    return value;
  } else if (Array.isArray(value)) {
    return value.map((item) => recursivelyReplaceColor(item, colorScheme)) as unknown as T;
  } else if (typeof value === 'object' && value !== null) {
    const newValue: Record<string, unknown> = {};
    for (const key in value) {
      // Use appropriate type checking to avoid the "string index" TypeScript error
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        newValue[String(key)] = recursivelyReplaceColor((value as Record<string, unknown>)[String(key)], colorScheme);
      }
    }
    return newValue as T;
  }

  return value;
}
